import { Button } from "@/components/ui/button";
import { Separator } from "@/components/ui/separator";
import React, { useCallback, useEffect, useState } from "react";
import FareInformation from "./fare-details";
import useFlightBookingStore from "@/lib/useFlightBookingStore";
import Image from "next/image";
import { useRouter, useSearchParams } from "next/navigation";
import { SegmentInfo, TotalPriceInfo } from "@/types/search/flight";
import { ChevronDown, ChevronUp, Loader2, Plane } from "lucide-react";
import FlightLine from "./flight-line";
import {
  Carousel,
  CarouselContent,
  CarouselNext,
  CarouselPrevious,
} from "@/components/ui/carousel";
import FareDetails from "./fareDetails";
import { FlightDetailsDialog } from "./flightDetailsDialog";

interface ComboFlightsDetailsCardProps {
  data: any;
  defaultFare: any;
  isCombo?: boolean;
  routeDataInfo?: any;
  manualBlockId?: string;
  manualBlockData?: any;
  matchedFlights?: any[];
  isMatched?: boolean;
  matchScore?: number;
  readOnly?: boolean;
  shareId?: string;
}

interface Fare {
  fare: any;
  id: string;
  fd: {
    ADULT: {
      fC: {
        TF: number;
      };
      cc: string;
      mi: boolean;
      rT: number;
    };
  };
}

const ComboFlightCard: React.FC<ComboFlightsDetailsCardProps> = ({
  data,
  defaultFare,
  isCombo,
  routeDataInfo,
  manualBlockId,
  manualBlockData,
  matchedFlights = [],
  isMatched = false,
  matchScore = 0,
  readOnly = false,
  shareId,
}) => {
  const [showAll, setShowAll] = useState(false);
  const [show, setShow] = useState(false);
  const {
    selectedMulticityFlights,
    setSelectedFlights,
    storeSearchParam,
    isViewOpen,
    isEmailOpen,
    setViewData,
    viewData,
    isWhatsAppOpen,
  }: any = useFlightBookingStore();
  const { push } = useRouter();
  const search = useSearchParams();
  const [isLoading, setIsLoading] = useState<boolean>(false);

  const { sI, totalPriceList } = data;
  const { fd } = totalPriceList[0];

  const formatDateAndTime = (dateTimeString: string) => {
    const dateObj = new Date(dateTimeString);
    const day = dateObj.getDate();
    const month = dateObj.toLocaleString("default", { month: "short" });
    const formattedDate = `${day} ${month}`;
    const hours = dateObj.getHours().toString().padStart(2, "0");
    const minutes = dateObj.getMinutes().toString().padStart(2, "0");
    const formattedTime = `${hours}:${minutes}`;
    return { formattedDate, formattedTime };
  };

  const formatDuration = (minutes: number) => {
    const hours = Math.floor(minutes / 60);
    const remainingMinutes = minutes % 60;
    return `${hours}h ${remainingMinutes}m`;
  };

  const displayedFares = showAll ? totalPriceList : totalPriceList.slice(0, 1);
  const [selectedFare, setSelectedFare] = useState<TotalPriceInfo | null>(
    defaultFare || totalPriceList?.[0] || null
  );
  const headerFares = readOnly
    ? selectedFare
      ? [selectedFare]
      : totalPriceList.slice(0, 1)
    : displayedFares;

  const handleFareChange = useCallback(
    async (fare: TotalPriceInfo) => {
      setSelectedFare(fare);
    },
    [setSelectedFare, selectedMulticityFlights]
  );

  const handleOneWaybookFlight = () => {
    const isReschedule = search.get("reschedule") === "true";
    const oldBookingId = search.get("oldBookingId");

    let url = `/flight-review/${selectedFare?.id}`;
    const params = new URLSearchParams();

    if (isReschedule) {
      params.set("reschedule", "true");
      if (oldBookingId) params.set("oldBookingId", oldBookingId);
      const paxIds = search.get("paxIds");
      if (paxIds) params.set("paxIds", paxIds);
    }

    if (manualBlockId) {
      url += `/manual-block/${manualBlockId}`;
    }

    if (params.toString()) {
      url += `?${params.toString()}`;
    }

    push(url);
    storeSearchParam(search);
  };

  const handleBookFlight = async () => {
    try {
      await handleOneWaybookFlight();
    } catch (error) {
      console.error("Error booking flight:", error);
    }
  };

  const firstDeparture = sI[0];
  const lastArrival = sI[sI.length - 1];

  const flightDetail = firstDeparture.fD;
  const flightImg = `/AirlinesLogo/${flightDetail.aI.code}.png`;
  const totalTravelTimeMinutes =
    firstDeparture.duration +
    (lastArrival.duration ?? 0) +
    (firstDeparture.cT ?? 0);

  const groupSegmentsBySequence = (
    segments: SegmentInfo[]
  ): SegmentInfo[][] => {
    const groupedSegments: SegmentInfo[][] = [];
    let currentGroup: SegmentInfo[] = [];

    segments.forEach((segment, index) => {
      // If we find sN = 0 and it's not the first segment
      if (segment.sN === 0 && index !== 0) {
        // Save the previous group if it's not empty
        if (currentGroup.length > 0) {
          groupedSegments.push([...currentGroup]);
          currentGroup = [];
        }
      }
      currentGroup.push(segment);
    });

    // Don't forget to add the last group
    if (currentGroup.length > 0) {
      groupedSegments.push(currentGroup);
    }

    return groupedSegments;
  };

  const groupedSegments = groupSegmentsBySequence(sI);

  // Helper function to get flight identifier - must match page.tsx logic
  const getFlightId = (flight: any) => {
    // First check if flight already has an ID (set by page.tsx)
    if (flight.id) {
      return flight.id;
    }
    // Try to get ID from totalPriceList first (fare ID)
    if (flight.totalPriceList?.[0]?.id) {
      return flight.totalPriceList[0].id;
    }
    // Fallback to creating ID from segment IDs
    if (flight.sI?.length > 0) {
      return flight.sI.map((segment: any) => segment.id).join("-");
    }
    // Last fallback - use flight number and route
    if (flight.sI?.[0]) {
      const firstSegment = flight.sI[0];
      return `${firstSegment.fD?.fN || "unknown"}-${firstSegment.da?.code || "unknown"
        }-${firstSegment.aa?.code || "unknown"}`;
    }
    return "unknown";
  };

  // Check if this flight is a matched flight from manual block
  const isMatchedFlight = isMatched;
  const matchInfo = matchedFlights.find(
    (match) => match.flight.id === getFlightId(data)
  );

  // Debug logging for manual block mode
  React.useEffect(() => {
    if (manualBlockId) {
      console.log("Combo flight card data:", {
        flightId: getFlightId(data),
        isMatchedFlight,
        matchInfo,
        matchScore,
        matchedFlightsCount: matchedFlights.length,
      });
    }
  }, [
    manualBlockId,
    getFlightId(data),
    isMatchedFlight,
    matchInfo,
    matchScore,
    matchedFlights.length,
  ]);

  // Add this helper function to calculate stops
  const calculateStops = (segments: SegmentInfo[]) => {
    if (!segments || segments.length === 0) return 0;

    // For each route in combo, stops = number of segments with same sN - 1
    const stops = segments.length - 1;
    return stops;
  };

  // Get match data for this flight
  const getMatchData = () => {
    if (!manualBlockId || !matchedFlights.length) return null;
    return matchedFlights.find(
      (match: any) => match.flight.id === getFlightId(data)
    );
  };

  const matchData = getMatchData();

  // Auto-select matched flight fare when in manual block mode
  React.useEffect(() => {
    if (manualBlockId && matchData && totalPriceList.length > 0) {
      // Auto-select the first fare of matched flights
      const fareToSelect = totalPriceList[0];
      if (
        fareToSelect &&
        (!selectedFare || selectedFare.id !== fareToSelect.id)
      ) {
        console.log(
          `Auto-selecting fare for matched combo flight: ${getFlightId(data)}`
        );
        setSelectedFare(fareToSelect);
      }
    }
  }, [
    manualBlockId,
    matchData,
    totalPriceList,
    selectedFare,
    getFlightId(data),
  ]);

  return (
    <div
      className={`rounded-lg ${!isCombo && "p-4 shadow"} relative ${matchData?.isPerfectMatch
        ? "bg-gradient-to-r from-emerald-50 to-green-50 ring-2 ring-emerald-500"
        : matchData?.isExactMatch
          ? "bg-green-50 ring-2 ring-green-500"
          : matchData
            ? "bg-blue-50 ring-1 ring-blue-300"
            : "bg-white"
        }`}
      data-flight-id={getFlightId(data)}
    >
      {/* Match Badge */}
      {matchData && (
        <div className="absolute top-2 right-2 z-10">
          {matchData.isPerfectMatch ? (
            <div className="bg-gradient-to-r from-emerald-500 to-green-500 text-white text-xs px-3 py-1 rounded-full font-bold shadow-lg">
              ⭐ PERFECT MATCH
            </div>
          ) : matchData.isExactMatch ? (
            <div className="bg-green-500 text-white text-xs px-2 py-1 rounded-full font-semibold shadow-md">
              EXACT MATCH
            </div>
          ) : (
            <div className="bg-blue-500 text-white text-xs px-2 py-1 rounded-full font-medium shadow-md">
              MATCH {Math.round(matchData.matchScore)}%
            </div>
          )}
        </div>
      )}

      <div className="space-y-4"></div>
      <div className="flex flex-col md:flex-row justify-between">
        <div className="basis-[65%] lg:basis-[75%] mb-2.5 lg:mb-0">
          <div className="flex flex-col justify-between h-[100%] relative">
            <div>
              <div className="py-3 lg:px-3 lg:py-4">
                <div className="flex flex-col md:flex-row md:justify-between items-start gap-2 xl:gap-4">
                  {/* Airline Info */}
                  <div className="flex items-center gap-3 basis-[22%] lg:basis-[18%]">
                    <div className="relative">
                      <Image
                        src={flightImg}
                        alt={flightDetail.aI.name}
                        width={40}
                        height={40}
                        className="rounded-full"
                      />
                      <span className="absolute -bottom-1 -right-1 bg-primary text-white text-[10px] rounded-full px-1.5 py-0.5">
                        {flightDetail.aI.code}
                      </span>
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-800 text-sm sm:text-base">
                        {flightDetail.aI.name}
                      </h3>
                      <p className="text-xs text-gray-500">{flightDetail.fN}</p>
                    </div>
                  </div>
                  <div className="flex flex-col gap-2">
                    {groupedSegments.map((group, groupIndex) => {
                      const firstDeparture = group[0];
                      const lastArrival = group[group.length - 1];

                      const flightDetail = firstDeparture.fD;
                      const { da, dt } = firstDeparture;
                      const {
                        formattedTime: deparTime,
                        formattedDate: deparDate,
                      } = formatDateAndTime(dt);
                      const { at, aa } = lastArrival;
                      const {
                        formattedTime: arriveTime,
                        formattedDate: arriveDate,
                      } = formatDateAndTime(at);

                      const totalTravelTimeMinutes =
                        group.length < 2
                          ? firstDeparture.duration
                          : firstDeparture.duration +
                          (lastArrival.duration ?? 0) +
                          (firstDeparture.cT ?? 0);

                      return (
                        <div key={groupIndex}>
                          {" "}
                          <div className="flex items-start justify-around w-[100%] basis-[100%] md:basis-[70%] lg:basis-[76%]">
                            {/* Departure */}
                            <div className="flex-shrink-0 w-auto xl:w-[100px]">
                              <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                                {deparTime}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                                {deparDate}
                              </div>
                              <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                                {/* <div>{da.city}</div> */}
                                <div className="flex items-center gap-1 flex-wrap">
                                  <span>({da.code})</span>
                                </div>
                              </div>
                            </div>

                            {/* Flight Path */}
                            <div className="flex flex-col items-center px-2 sm:px-4 mx-2 sm:mx-4 flex-shrink-0 mt-1.5 w-[160px] xl:w-[200px]">
                              <span className="text-[10px] sm:text-xs text-primary mb-1 lg:mb-2 font-semibold">
                                {totalTravelTimeMinutes > 0
                                  ? formatDuration(totalTravelTimeMinutes)
                                  : "0m"}
                              </span>
                              <FlightLine />
                              <span className="text-[10px] sm:text-xs text-gray-700 mt-2 lg:mt-3 font-bold">
                                {(() => {
                                  const stops = calculateStops(group);
                                  if (stops === 0) return "Non-Stop";
                                  if (stops === 1) return "1 Stop";
                                  return `${stops} Stops`;
                                })()}
                              </span>
                            </div>

                            {/* Arrival */}
                            {/* flex-shrink-0 */}
                            <div className="text-right sm:text-center xl:w-[150px] sm:w-auto">
                              <div className="text-sm lg:text-[18px] font-bold text-gray-900">
                                {arriveTime}
                              </div>
                              <div className="text-xs sm:text-sm text-gray-500 font-medium mt-0.5">
                                {arriveDate}
                              </div>
                              <div className="text-[11px] sm:text-xs text-gray-600 mt-1">
                                {/* <div>{aa.city}</div> */}
                                <div className="flex items-center gap-1 justify-end sm:justify-center flex-wrap">
                                  <span>({aa.code})</span>
                                </div>
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div className="text-right flex flex-col items-start gap-1 basis-[25%] pt-3 relative">
          {headerFares.map((fare: any, index: number) => {
            const totalPrice = fare.fd.ADULT.fC.TF;
            return (
              <div
                className="flex justify-between items-center w-full"
                key={index}
              >
                <div className="flex flex-col mb-1 w-[100%]" key={index}>
                  <div className="flex space-x-2">
                    {!readOnly && (
                    <input
                      type="radio"
                      id={`fare-${"combo"}-${index}`}
                      className="accent-primary appearance-auto"
                      value={fare.fd.ADULT.fC.TF}
                      checked={selectedFare?.id === fare.id}
                      onChange={() => handleFareChange(fare)}
                      style={{ WebkitAppearance: "radio", appearance: "radio" }}
                    />
                    )}
                    <label htmlFor={`fare-${"combo"}-${index}`}>
                      <p className="font-bold text-[#0e1e2b] text-sm lg:text-[20px] mb-1">
                        {new Intl.NumberFormat("en-IN", {
                          style: "currency",
                          currency: "INR",
                        }).format(totalPrice)}
                      </p>
                    </label>
                  </div>
                  <Separator className="mt-2" />
                </div>
              </div>
            );
          })}

          <div className="flex justify-start">
            <Button
              className="text-sm font-bold text-primary bg-white shadow-md border-2 border-primary"
              onClick={() => setShow(!show)}
            >
              {show ? "Hide Details" : "Show Details"}
            </Button>
          </div>

          <p>
            <FlightDetailsDialog sI={sI} />
          </p>
        </div>
      </div>
      <div>
        {selectedFare && (
          <div className="">
            {show && (
              <div className="p-4">
                <Carousel
                  opts={{
                    align: "start",
                    loop: false,
                  }}
                  className="w-full"
                >
                  <CarouselContent className="-ml-2 md:-ml-4">
                    {totalPriceList.map((fares: any) => (
                      <FareDetails
                        fares={fares}
                        key={fares.id}
                        totalPriceList={totalPriceList}
                        Combo={"Combo"}
                        sI={sI}
                        selectedFare={selectedFare}
                        totalTravelTimeMinutes={totalTravelTimeMinutes}
                        handlerFaresRulesBooks={handleBookFlight}
                        readOnly={readOnly}
                        shareId={shareId}
                        onSelectFare={readOnly ? setSelectedFare : undefined}
                      />
                    ))}
                  </CarouselContent>
                  <CarouselPrevious />
                  <CarouselNext />
                </Carousel>
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default ComboFlightCard;
